import { ExpressionPart } from 'src/models/calculations/CalculationDetailsResult';
import { SavingStatus } from 'src/models/common/SavingStatus';
import WorksheetTotal from 'src/models/tp-allocation/WorksheetTotal';
import { CreateDASTotalRequest, DASTotal, UpdateDASTotalRequest } from 'src/models/derived-allocation-sheet/DASTotal';

/*
 * Maps Step 2 totals to and from TPA worksheet totals, so the TPA totals reducer and grid can be reused for
 * derived allocation sheets.
 */

// Unique suffix for unsaved row ids.
let newTotalCount = 0;

/** Drops view-only members so a token matches the API's expression token shape. */
const toApiTokens = (tokens: ExpressionPart[]): ExpressionPart[] => tokens.map((t) => ({
    type: t.type,
    value: t.value,
    ...(t.subTokens ? { subTokens: toApiTokens(t.subTokens) } : {}),
}));

/** Editability is left to the view, which knows the sheet's status and version. */
export function toWorksheetTotal(total: DASTotal, sequence: number): WorksheetTotal {
    const row: Partial<WorksheetTotal> = {
        ...total,
        description: total.description ?? '',
        expression: total.expression ?? '',
        expressionTokens: total.expressionTokens ?? [],
        value: total.value != null ? String(total.value) : undefined,
        parentEntityId: total.sheetId,
        sequence,
        isNew: false,
        isBeingEdited: false,
        savingStatus: SavingStatus.Saved,
    };
    return row as WorksheetTotal;
}

/** Totals of one version, numbered in list order. */
export function toWorksheetTotals(totals: DASTotal[]): WorksheetTotal[] {
    return totals.map((total, index) => toWorksheetTotal(total, index + 1));
}

/** Create requires tokens, so a row without any sends an empty list. */
export function toCreateDASTotalRequest(sheetId: string, total: WorksheetTotal): CreateDASTotalRequest {
    return {
        sheetId,
        description: total.description?.trim() ?? '',
        expression: total.expression ?? '',
        expressionTokens: toApiTokens(total.expressionTokens ?? []),
    };
}

/** Update sends only the members the row defines. */
export function toUpdateDASTotalRequest(sheetId: string, total: WorksheetTotal): UpdateDASTotalRequest {
    const description = total.description?.trim();
    return {
        sheetId,
        totalId: total.totalId,
        ...(description != null ? { description } : {}),
        ...(total.expression != null ? { expression: total.expression } : {}),
        ...(total.expressionTokens != null ? { expressionTokens: toApiTokens(total.expressionTokens) } : {}),
    };
}

/** The saved row, keeping the id it had before saving; throws when the response carries no row. */
export function toSavedWorksheetTotal(saved: DASTotal | null, sent: WorksheetTotal): WorksheetTotal {
    if (!saved) {
        throw new Error('The total could not be saved.');
    }
    return { ...toWorksheetTotal(saved, sent.sequence), unsavedId: sent.totalId };
}

/** An unsaved row for the totals grid. Its id is unique for the life of the page, also after a removal. */
export function newDasTotal(sequence: number, sheetId: string): WorksheetTotal {
    newTotalCount += 1;
    return {
        totalId: `newTotal-${newTotalCount}`,
        sequence,
        parentEntityId: sheetId,
        isBeingEdited: true,
        isEditable: true,
        isNew: true,
        expression: '',
        description: '',
    } as WorksheetTotal;
}
